import AudienceMessage from '@/components/domains/chat/chat-audience/AudienceMessage.vue';
import ConferenceChatComing from '@/components/domains/chat/chat-conference/ConferenceChatComing.vue';
import GeneralMessageChatComing from '@/components/domains/chat/chat-general/GeneralMessageChatComing.vue';
import GeneralMessageChatGoing from '@/components/domains/chat/chat-general/GeneralMessageChatGoing.vue';
import GeneralMessageDate from '@/components/domains/chat/chat-general/GeneralMessageDate.vue';
import GeneralMessageTraffic from '@/components/domains/chat/chat-general/GeneralMessageTraffic.vue';
import { TypeCall } from '@/types/common';
import { LanguageCode } from '@/types/common';
import {
  ChatFooterOptions,
  ChatFooterViewType,
  ChatOptions,
  ChatSettingOptions,
  ChatUIState,
} from '@/types/components';
import { MCSSubUser } from '@/types/socket';
export const CHAT_FOOTER_PRESETS: Record<ChatFooterViewType, ChatFooterOptions[]> = {
  GENERAL: ['MICROPHONE', 'SPEAKER', 'LANGUAGE', 'SETTING', 'EXIT'],
  CONFERENCE: ['MICROPHONE', 'SPEAKER', 'LANGUAGE', 'SETTING', 'EXIT'],
  AUDIENCE: ['MICROPHONE', 'LANGUAGE', 'SETTING', 'EXIT'],
  MEETING: ['MICROPHONE', 'SYSTEM_AUDIO_MICROPHONE', 'SPEAKER', 'LANGUAGE', 'SETTING', 'EXIT'],
  MEETING_PLUS: ['MICROPHONE', 'SYSTEM_AUDIO_MICROPHONE', 'SPEAKER', 'LANGUAGE', 'SETTING', 'EXIT'],
  SINGLE: ['MICROPHONE', 'SETTING', 'EXIT'],
  SCREEN: [],
  SUBTITLE: [],

  DM: ['MICROPHONE', 'SPEAKER', 'LANGUAGE', 'DM', 'SETTING', 'EXIT'],
  MCSP: ['LANGUAGE', 'MCSP', 'SETTING'],
  MCS: ['LANGUAGE', 'MCS', 'SETTING'],
};

export const GENERAL_MESSAGE_COMPONENT_MAP = {
  TRAFFIC: GeneralMessageTraffic,
  DATE: GeneralMessageDate,
  CHAT_GOING: GeneralMessageChatGoing,
  CHAT_COMING: GeneralMessageChatComing,
} as const;

export const CONFERENCE_MESSAGE_HOST_COMPONENT_MAP = {
  TRAFFIC: GeneralMessageTraffic,
  DATE: GeneralMessageDate,
  CHAT_GOING: GeneralMessageChatGoing,
  CHAT_COMING: GeneralMessageChatComing,
} as const;

export const CONFERENCE_MESSAGE_GUEST_COMPONENT_MAP = {
  TRAFFIC: GeneralMessageTraffic,
  DATE: GeneralMessageDate,
  CHAT_GOING: ConferenceChatComing,
  CHAT_COMING: ConferenceChatComing,
} as const;

export const AUDIENCE_MESSAGE_COMPONENT_MAP = {
  CHAT_GOING: AudienceMessage,
  CHAT_COMING: AudienceMessage,
} as const;

export const MEETING_MESSAGE_COMPONENT_MAP = {
  CHAT_GOING: AudienceMessage,
  CHAT_COMING: AudienceMessage,
} as const;

export const MEETING_PLUS_MESSAGE_COMPONENT_MAP = {
  CHAT_GOING: AudienceMessage,
  CHAT_COMING: AudienceMessage,
} as const;

// 방을 생성해야 하는 통역방 타입
export const CREATE_CHAT_TYPES = ['GENERAL', 'CONFERENCE', 'MEETING', 'MEETING_PLUS'] as const;

export const CHAT_THEME = {
  GENERAL: 'light',
  CONFERENCE: 'light',
  AUDIENCE: 'dark',
  MEETING: 'dark',
  SINGLE: 'light',
} as const;

export const CHAT_MODES = [
  {
    id: 'SCB_C',
    key: 'GENERAL',
    callType: 'GENERAL',
    className: 'general',
    emoji: '💬',
    titleKey: 'label_general',
    descriptionKey: 'label_general_description',
  },
  {
    id: 'SCB_P',
    key: 'CONFERENCE',
    callType: 'CONFERENCE',
    className: 'conference',
    emoji: '🗣️',
    titleKey: 'label_conference',
    descriptionKey: 'label_conference_description',
  },
  {
    id: 'SCB_M',
    key: 'MEETING',
    callType: 'MEETING',
    className: 'meeting',
    emoji: '👥',
    titleKey: 'label_meeting',
    descriptionKey: 'label_meeting_description',
  },
  {
    id: 'SCB_MP',
    key: 'MEETING_PLUS',
    callType: 'MEETING',
    className: 'meeting-plus',
    emoji: '👥',
    titleKey: 'label_meeting_plus',
    descriptionKey: 'label_meeting_plus_description',
  },
];

export enum INTERPRETATION_SPEED {
  NORMAL = 0,
  FASTER = 1,
  FASTEST = 2,
}

export const CHAT_TYPES = {
  GENERAL: 'GENERAL', // 일반 대화용
  MEETING: 'MEETING', // 미팅용
  MEETING_PLUS: 'MEETING_PLUS', // 미팅플러스용 (스크린/자막 지원)
  CONFERENCE: 'CONFERENCE', // 컨퍼런스용
  SINGLE: 'SINGLE', // 1:1 싱글용
  AUDIENCE: 'AUDIENCE', // 청취용
  SCREEN: 'SCREEN', // 스크린 모드
  SUBTITLE: 'SUBTITLE', // 자막 모드
} as const;

export const CHAT_PRICE_KEY_MAP = {
  GENERAL: 'SCB_C',
  MEETING: 'SCB_M',
  MEETING_PLUS: 'SCB_MP',
  CONFERENCE: 'SCB_P',
  SINGLE: 'SCB_S',
  AUDIENCE: 'SCB_A',
} as const;

export const CHAT_LABEL_KEY_MAP = {
  GENERAL: 'label_general',
  MEETING: 'label_meeting',
  MEETING_PLUS: 'label_meeting_plus',
  CONFERENCE: 'label_conference',
  SINGLE: 'label_single',
  AUDIENCE: 'label_audience',
} as const;

/**
 * @desc 크레딧 가격 정보 매핑
 */
export const SERVICE_PRICE_MAP = {
  // Conversation - SCB_C2, SCB_C10, SCB_C30
  SCB_C2: {
    prefix: 'SCB_C',
    limitAttendee: 2,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_general',
  },
  SCB_C10: {
    prefix: 'SCB_C',
    limitAttendee: 10,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_general',
  },
  SCB_C30: {
    prefix: 'SCB_C',
    limitAttendee: 30,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_general',
  },
  // Conference - SCB_P30, SCB_P50, SCB_P100, SCB_P300, SCB_P500, SCB_P1000, SCB_P2000, SCB_P3000
  SCB_P30: {
    prefix: 'SCB_P',
    limitAttendee: 30,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_conference',
  },
  SCB_P50: {
    prefix: 'SCB_P',
    limitAttendee: 50,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_conference',
  },
  SCB_P100: {
    prefix: 'SCB_P',
    limitAttendee: 100,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_conference',
  },
  SCB_P300: {
    prefix: 'SCB_P',
    limitAttendee: 300,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_conference',
  },
  SCB_P500: {
    prefix: 'SCB_P',
    limitAttendee: 500,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_conference',
  },
  SCB_P1000: {
    prefix: 'SCB_P',
    limitAttendee: 1000,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_conference',
  },
  SCB_P2000: {
    prefix: 'SCB_P',
    limitAttendee: 2000,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_conference',
  },
  SCB_P3000: {
    prefix: 'SCB_P',
    limitAttendee: 3000,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_conference',
  },
  // One-on-One
  SCB_S1: {
    prefix: 'SCB_S',
    limitAttendee: 1,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_single',
  },
  // Audience
  SCB_A1: {
    prefix: 'SCB_A',
    limitAttendee: 1,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_audience',
  },
  // Meeting
  SCB_M30: {
    prefix: 'SCB_M',
    limitAttendee: 30,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_meeting',
  },
  // Meeting Plus
  SCB_MP30: {
    prefix: 'SCB_MP',
    limitAttendee: 30,
    prepaymentUnitPrice: 0,
    postpaidUnitPrice: 0,
    labelKey: 'label_meeting_plus',
  },
} as const;

export const MANUAL_URL_MAP = {
  ko: 'https://sconai.gitbook.io/sconai',
  en: 'https://sconai.gitbook.io/sconai/manual/en',
} as const;

export const MANUAL_PARAMS_MAP = {
  CONFERENCE: 'conference',
  MEETING: 'meeting',
  AUDIENCE: 'audience',
  GENERAL: 'conversation',
  SINGLE: 'single',
} as const;

/**
 * @desc plan ID 생성 ({타입ID}{인원수} 형태, 예: SCB_MP30, SCB_C10)
 */
export const generatePlanId = (id: string, maxParticipants: number): string => {
  return `${id}${maxParticipants}`;
};

const CHAT_MODE_KEY_BY_ID = new Map(CHAT_MODES.map((m) => [m.id, m.key]));

/**
 * @desc id(SCB_MP 등)로 CHAT_MODES의 key(MEETING_PLUS 등) 조회
 */
export const getChatModeKeyById = (id: string): string => {
  return CHAT_MODE_KEY_BY_ID.get(id) ?? '';
};

export const DEFAULT_CHAT_CREATE_FORM = {
  id: '',
  callType: '',
  title: '',
  titles: '',
  description: '',
  selectedGroup: null,
  maxParticipants: 0,
  keywords: [],
  options: {
    showAttendee: true,
    displayVoiceRegister: true,
    screenMode: true,
    subtitleMode: true,
    showGuestMic: true,
    useWithoutHost: false,
    MCSP: false,
    MCS: false,
  },
};

export const CHAT_VISIBLE_OPTION_PRESETS = {
  GENERAL: ['showAttendee', 'displayVoiceRegister', 'showGuestMic', 'useWithoutHost'],
  CONFERENCE: ['showAttendee', 'screenMode', 'subtitleMode'],
  MEETING: ['screenMode', 'subtitleMode'],
  MEETING_PLUS: ['screenMode', 'subtitleMode'],
  AUDIENCE: [],
  SINGLE: [],
};

export const CHAT_QR_OPTIONS: ChatOptions = {
  screenMode: false,
  subtitleMode: false,
  showGuestMic: false,
  useWithoutHost: false,
  screenHeight: false,
  directMessage: false,
  MCS: false,
  MCSP: false,
  systemAudio: false,
  showAttendee: false,
  displayVoiceRegister: false,
  refreshChatId: false,
  forceVoiceRegister: false,
  endCallDelaySeconds: 60,
  daysToStoreLog: 0,
} as const;

export const CHAT_OPTION_PRESETS = {
  // 대화
  GENERAL: {
    showAttendee: false,
    displayVoiceRegister: true,
    screenMode: false,
    showGuestMic: true,
    useWithoutHost: false,
    MCSP: false,
    MCS: false,
  },
  // 컨퍼런스
  CONFERENCE: {
    showAttendee: false,
    displayVoiceRegister: false,
    screenMode: true,
    subtitleMode: true,
    showGuestMic: false,
    useWithoutHost: false,
    MCSP: false,
    MCS: false,
  },
  // 미팅
  MEETING: {
    showAttendee: false,
    displayVoiceRegister: false,
    screenMode: false,
    subtitleMode: false,
    showGuestMic: false,
    useWithoutHost: false,
    simpleMcs: false,
  },
  // 미팅플러스
  MEETING_PLUS: {
    showAttendee: false,
    displayVoiceRegister: false,
    screenMode: true,
    subtitleMode: true,
    showGuestMic: false,
    useWithoutHost: false,
    simpleMcs: false,
  },
  // 청취
  AUDIENCE: {
    showAttendee: false,
    displayVoiceRegister: false,
    screenMode: false,
    showGuestMic: false,
    useWithoutHost: false,
    MCSP: false,
    MCS: false,
  },
  // 싱글
  SINGLE: {
    showAttendee: false,
    displayVoiceRegister: false,
    screenMode: true,
    showGuestMic: false,
    useWithoutHost: false,
    MCSP: false,
    MCS: false,
  },
} as const;

export const DEFAULT_CHAT_UI_STATE = {
  state: {} as Readonly<ChatUIState>,
  actions: {
    toggleDm: () => {},
    toggleMCSPController: () => {},
    toggleMCSController: () => {},
    toggleMicSecretController: () => {},
    toggleButtonMyLanguage: () => {},
    toggleSecretLangaugeChange: () => {},
  },
} as const;

export const CHAT_SETTING_OPTIONS: Record<TypeCall, ChatSettingOptions[]> = {
  GENERAL: [
    'MUTE_GUEST',
    'FAST_INTERPRETATION',
    'TTS',
    'ONLY_MY_LANGUAGE',
    'TEXT_SIZE_CONTROL',
    'ATTENDEE_LIST',
    'INTERPRETATION_SPEED_CONTROL',
    'AUXILIARY_DISPLAY_CONTROL',
    'USER_GUIDE',
  ],
  CONFERENCE: [
    'MUTE_GUEST',
    'FAST_INTERPRETATION',
    'TTS',
    'ONLY_MY_LANGUAGE',
    'TEXT_SIZE_CONTROL',
    'ATTENDEE_LIST',
    'INTERPRETATION_SPEED_CONTROL',
    'AUXILIARY_DISPLAY_CONTROL',
    'USER_GUIDE',
  ],
  AUDIENCE: ['TEXT_SIZE_CONTROL', 'VOICE_CONTROL', 'DOWNLOAD_HISTORY', 'INTERPRETATION_SPEED_CONTROL', 'USER_GUIDE'],
  MEETING: ['TEXT_SIZE_CONTROL', 'INTERPRETATION_SPEED_CONTROL', 'AUXILIARY_DISPLAY_CONTROL', 'USER_GUIDE'],
  MEETING_PLUS: ['TEXT_SIZE_CONTROL', 'INTERPRETATION_SPEED_CONTROL', 'AUXILIARY_DISPLAY_CONTROL', 'USER_GUIDE'],
  SINGLE: ['TEXT_SIZE_CONTROL', 'FACE_TO_FACE', 'INTERPRETATION_SPEED_CONTROL', 'DOWNLOAD_HISTORY', 'USER_GUIDE'],
  SCREEN: ['TEXT_SIZE_CONTROL'],
  SUBTITLE: [],
} as const;

export const CHAT_TEXT_MULTIPLIERS = [0.875, 1, 1.125, 1.25, 1.375, 1.5, 1.625, 1.75, 1.875, 2.0] as const;

export const CHAT_KIND = Object.freeze({
  MEETING: 'meeting',
  CONFERENCE: 'conference',
  GENERAL: 'general',
  AUDIENCE: 'audience',
  SINGLE: 'single',
  ALL: ['meeting', 'conference', 'general', 'audience', 'single'],
  isMeeting: (type) => type === CHAT_KIND.MEETING || type === CHAT_KIND.MEETING.toUpperCase(),
  isConference: (type) => type === CHAT_KIND.CONFERENCE || type === CHAT_KIND.CONFERENCE.toUpperCase(),
  isGeneral: (type) => type === CHAT_KIND.GENERAL || type === CHAT_KIND.GENERAL.toUpperCase(),
  isAudience: (type) => type === CHAT_KIND.AUDIENCE || type === CHAT_KIND.AUDIENCE.toUpperCase(),
  isSingle: (type) => type === CHAT_KIND.SINGLE || type === CHAT_KIND.SINGLE.toUpperCase(),
  isOneOf: (type, kinds) => kinds.includes(type.toLowerCase()),
  isValid: (type) => CHAT_KIND.ALL.includes(type.toLowerCase()),
});

export const NO_SPACE_LANGS = new Set<LanguageCode>(['zh', 'ja', 'th']);

export const getInitialSubUser = (): MCSSubUser => ({
  loginId: '',
  userId: '',
  nickname: '',
  authToken: '',
});
